import { Injectable } from '@angular/core';
import { BillClusterService, BillViewModel } from '../../../../Annual-Details/Taxes/bill-cluster.service';
import { Payment } from '../../../../Annual-Details/Taxes/payment.model';

import moment from 'moment';
import { cloneDeep, every, filter, find, flatMap, includes, isNumber, maxBy, minBy, reduce, some, words } from 'lodash';

import DetailsInstallmentMessageItem = Weissman.Model.Workflow.DocumentIntake.OcrResponse.Message.DetailsInstallmentMessageItem;
import { Bill } from 'src/app/Annual-Details/Taxes/bill.model';
import { AssessmentViewModel } from 'src/app/Annual-Details/Assessments/assessment.service';
import { AssessmentComponent } from 'src/app/Annual-Details/Assessments/assessment.component';
import { PropertyType } from 'src/app/constants.new';
import { AnnualAssessmentDetail } from 'src/app/Annual-Details/Assessments/assessment.model';
import { IdpCard } from './Detail-Modal/Card/idpDetailCard.component';

export interface MappingExplanations {
    av?: string;
    fairMarketValue?: string;
    exem?: string;
    cap?: string;
    lowConfidenceReasons: string[];
    components?: {
        name: string;
        explanation: string;
    }[];
}

class PostMappingData {
    constructor() {
        this.disableApplyAll = false;
        this.explanations = {
            lowConfidenceReasons: [],
            components: [],
        };
    }

    disableApplyAll: boolean;
    explanations: MappingExplanations;
}

interface AvailableComponent extends Weissman.Model.Assessments.AssessmentClassComponent {
    componentName: string;
}

interface AssessmentChildUpdates {
    calculateValues: { valueStr: string; component: AnnualAssessmentDetail; amount: number }[];
    addRowValues: { component: AvailableComponent; value: { [x: string]: number } }[];
}

const IMPS_ASSESSMENT_COMPONENT_ID = 2;
const PP_ASSESSMENT_COMPONENT_ID = 4;
const OTHER_ASSESSMENT_COMPONENT_ID = 3;
const EXEM_ASSESSMENT_COMPONENT_ID = 5;
const CAP_ASSESSMENT_COMPONENT_ID = 12;

const NORMAL_COMPONENT_TYPE = 1;
const EXEM_COMPONENT_TYPE = 2;
const ALT_COMPONENT_TYPE = 3;

const PROP_TYPE_COMP_MAP = {
    [PropertyType.RealEstate]: IMPS_ASSESSMENT_COMPONENT_ID,
    [PropertyType.Personal]: PP_ASSESSMENT_COMPONENT_ID,
    [PropertyType.Mineral]: OTHER_ASSESSMENT_COMPONENT_ID,
    [PropertyType.CentrallyAssessed]: OTHER_ASSESSMENT_COMPONENT_ID,
};

const EXTRA_COMP_MAP = {
    [EXEM_COMPONENT_TYPE]: EXEM_ASSESSMENT_COMPONENT_ID,
    [ALT_COMPONENT_TYPE]: CAP_ASSESSMENT_COMPONENT_ID,
};

@Injectable({ providedIn: 'root' })
export class IdpService {
    constructor(private readonly _billClusterService: BillClusterService) {}

    cardValues = [
        { name: 'Base Pay Amt', field: 'grossPayment', type: 'currency' },
        { name: 'Discount Amt', field: 'discountAmount', type: 'currency' },
        { name: 'Interest', field: 'interestAmount', type: 'currency' },
        { name: 'Penalty', field: 'penaltyAmount', type: 'currency' },
        { name: 'Due Date', field: 'dueDate', type: 'date' },
    ];

    billAmountDoesntAddUp(
        billDetail: BillViewModel,
        idpResults: Weissman.Model.Workflow.DocumentIntake.OcrResponse.API.TaxBillAPIResponse,
    ): boolean {
        const billDetailCopy = cloneDeep(billDetail);

        return some(billDetailCopy.model, (bill) => {
            this._mapBillFromIdp(bill, idpResults);

            const grossPaymentSum = reduce(
                bill.payments,
                (sum, payment) => new Decimal(sum).plus(payment.grossPayment).toNumber(),
                0,
            );

            return bill.billAmount !== grossPaymentSum;
        });
    }

    isDueDateMismatch(
        billDetail: BillViewModel,
        installments: Weissman.Model.Workflow.DocumentIntake.OcrResponse.API.InstallmentAPIItem[],
    ): boolean {
        return some(billDetail.model, (bill) => {
            const { installment, discountIndex } = this._getInstallmentAndDiscountIdx(installments, bill);

            return some(bill.payments, (payment: Payment, pmtIdx: number) => {
                const ocrPayment = installment?.details[pmtIdx];
                const possibleDueDate = ocrPayment?.discount[discountIndex]?.dueDate || ocrPayment?.dueDate;
                // If there is a payment due date, but it's not the same as the possible one
                // Do not overwrite, and lower the confidence
                return payment.dueDate && !moment(possibleDueDate).isSame(moment(payment.dueDate));
            });
        });
    }

    isMissingPaymentDueDate(
        installments: Weissman.Model.Workflow.DocumentIntake.OcrResponse.API.InstallmentAPIItem[],
    ): boolean {
        return some(installments, (installment) => {
            if (!installment.details.length) {
                return true;
            }

            return some(installment.details, (detail) => !detail.dueDate && every(detail.discount, (x) => !x.dueDate));
        });
    }

    mapBillVMFromIDP(
        billVM: BillViewModel,
        idpResults: Weissman.Model.Workflow.DocumentIntake.OcrResponse.API.TaxBillAPIResponse,
        paymentsFromModal?: IdpCard[],
    ): void {
        billVM.model.forEach((bill) => this._mapBillFromIdp(bill, idpResults, paymentsFromModal));
    }

    getDiscount(ocrPayment: DetailsInstallmentMessageItem, discountIndex: number): number {
        const discount = ocrPayment?.discount[discountIndex];

        if (discount?.discountAmount) {
            return discount?.discountAmount;
        } else if (discount?.netAmount && ocrPayment.amount) {
            return new Decimal(ocrPayment.amount).minus(discount?.netAmount ?? 0).toNumber();
        } else {
            return 0;
        }
    }

    async mapAssessmentVMFromIDP(
        assessmentVM: AssessmentViewModel,
        {
            appealDeadline,
            assessedValue,
            fairMarketValue,
            exemptions,
            cap,
        }: Weissman.Model.Workflow.DocumentIntake.Idp.Assessment.AssessmentRevisionItem,
        taxAuthority: Weissman.Model.Workflow.DocumentIntake.Idp.Assessment.TaxAuthorityAPIItem | undefined,
        assessmentChild: AssessmentComponent,
        dontMap: boolean,
        force?: boolean,
    ): Promise<PostMappingData> {
        console.log('assessmentVM', assessmentVM);
        console.log('assessed value', assessedValue);
        console.log('fair market value', fairMarketValue);
        console.log('exemptions', exemptions);
        console.log('cap', cap);

        assessmentVM.currentYear.calcProjected = false;

        let postMappingData = new PostMappingData();
        let exemPostMappingData = new PostMappingData();
        let altPostMappingData = new PostMappingData();

        const assessmentChildUpdates: AssessmentChildUpdates = {
            calculateValues: [],
            addRowValues: [],
        };

        const propertyTypeId: number = assessmentVM.components[0]?.propertyTypeID ?? 0;
        const enabledComponents = assessmentVM.components.filter((x) => x.enabled) as AvailableComponent[];
        const { idpComponents, valueStr, otherComponents } = assessedValue?.components.length
            ? {
                  idpComponents: assessedValue.components,
                  valueStr: 'av',
                  otherComponents: fairMarketValue?.components,
              }
            : {
                  idpComponents: fairMarketValue?.components,
                  valueStr: 'fairMarketValue',
                  otherComponents: assessedValue?.components,
              };

        const nonZeroIdpComponents = filter(idpComponents, (x) => x.amount);

        if (force) {
            if (nonZeroIdpComponents?.length) {
                postMappingData = await this._mapComponents(
                    nonZeroIdpComponents,
                    valueStr,
                    assessmentVM,
                    enabledComponents,
                    assessedValue?.amount,
                    assessmentChildUpdates,
                    otherComponents,
                    force,
                );
            } else {
                if (assessedValue?.amount) {
                    postMappingData = await this._divvyUpIdpTotal(
                        assessedValue.amount,
                        assessmentVM,
                        enabledComponents,
                        'av',
                        'assessedValue',
                        propertyTypeId,
                        NORMAL_COMPONENT_TYPE,
                        assessmentChildUpdates,
                    );
                } else if (taxAuthority?.assessedValueAmount) {
                    postMappingData = await this._divvyUpIdpTotal(
                        taxAuthority.assessedValueAmount,
                        assessmentVM,
                        enabledComponents,
                        'av',
                        'assessedValue',
                        propertyTypeId,
                        NORMAL_COMPONENT_TYPE,
                        assessmentChildUpdates,
                    );
                }

                // This used to be an else if, but changed due to https://dev.azure.com/ryanunity/PropertyPoint/_workitems/edit/319429
                // This used to be Number(fairMarketValue?.amount), but changed due to https://dev.azure.com/ryanunity/PropertyPoint/_workitems/edit/571601
                if (fairMarketValue?.amount) {
                    postMappingData = await this._divvyUpIdpTotal(
                        fairMarketValue.amount,
                        assessmentVM,
                        enabledComponents,
                        'fairMarketValue',
                        'fairMarketValue',
                        propertyTypeId,
                        NORMAL_COMPONENT_TYPE,
                        assessmentChildUpdates,
                    );
                }
            }
        } else {
            if (nonZeroIdpComponents?.length) {
                postMappingData = await this._mapComponents(
                    nonZeroIdpComponents,
                    valueStr,
                    assessmentVM,
                    enabledComponents,
                    assessedValue?.amount,
                    assessmentChildUpdates,
                    otherComponents,
                );
            } else if (assessedValue?.amount) {
                postMappingData = await this._divvyUpIdpTotal(
                    assessedValue.amount,
                    assessmentVM,
                    enabledComponents,
                    'av',
                    'assessedValue',
                    propertyTypeId,
                    NORMAL_COMPONENT_TYPE,
                    assessmentChildUpdates,
                );
            } else if (fairMarketValue?.amount) {
                postMappingData = await this._divvyUpIdpTotal(
                    fairMarketValue.amount,
                    assessmentVM,
                    enabledComponents,
                    'fairMarketValue',
                    'fairMarketValue',
                    propertyTypeId,
                    NORMAL_COMPONENT_TYPE,
                    assessmentChildUpdates,
                );
            } else if (taxAuthority?.assessedValueAmount) {
                postMappingData = await this._divvyUpIdpTotal(
                    taxAuthority.assessedValueAmount,
                    assessmentVM,
                    enabledComponents,
                    'av',
                    'assessedValue',
                    propertyTypeId,
                    NORMAL_COMPONENT_TYPE,
                    assessmentChildUpdates,
                );
            }
        }

        const nonZeroIdpExemComponents = filter(exemptions[0]?.components ?? [], (x) => x.amount);

        if (nonZeroIdpExemComponents.length) {
            exemPostMappingData = await this._mapComponents(
                nonZeroIdpExemComponents,
                'av',
                assessmentVM,
                enabledComponents,
                assessedValue?.amount,
                assessmentChildUpdates,
            );
        } else if (exemptions[0]?.amount) {
            exemPostMappingData = await this._divvyUpIdpTotal(
                exemptions[0].amount,
                assessmentVM,
                enabledComponents,
                'av',
                'assessedValue',
                propertyTypeId,
                EXEM_COMPONENT_TYPE,
                assessmentChildUpdates,
            );
        } else if (taxAuthority?.exemptionAmount) {
            exemPostMappingData = await this._divvyUpIdpTotal(
                taxAuthority.exemptionAmount,
                assessmentVM,
                enabledComponents,
                'av',
                'assessedValue',
                propertyTypeId,
                EXEM_COMPONENT_TYPE,
                assessmentChildUpdates,
            );
        }

        const nonZeroIdpCapComponents = filter(cap?.components ?? [], (x) => x.amount);

        if (nonZeroIdpCapComponents.length) {
            altPostMappingData = await this._mapComponents(
                nonZeroIdpCapComponents,
                'av',
                assessmentVM,
                enabledComponents,
                assessedValue?.amount,
                assessmentChildUpdates,
            );
        } else if (cap?.amount) {
            altPostMappingData = await this._divvyUpIdpTotal(
                cap.amount,
                assessmentVM,
                enabledComponents,
                'av',
                'assessedValue',
                propertyTypeId,
                ALT_COMPONENT_TYPE,
                assessmentChildUpdates,
            );
        }

        const lowConfidenceReasons = [
            ...postMappingData.explanations.lowConfidenceReasons,
            ...exemPostMappingData.explanations.lowConfidenceReasons,
            ...altPostMappingData.explanations.lowConfidenceReasons,
        ];

        // We only want to update the assessment if there are no low confidence reasons
        if (!dontMap && (lowConfidenceReasons.length === 0 || force)) {
            if (!assessmentChild.viewModel) {
                // On initial load the child has not had its view model bound yet; wait for change detection
                await new Promise<void>((resolve) => setTimeout(resolve));
            }

            for (const { valueStr, component, amount } of assessmentChildUpdates.calculateValues) {
                assessmentChild.calculate(valueStr, component, amount, true);
            }
            for (const { component, value } of assessmentChildUpdates.addRowValues) {
                await assessmentChild.addRow(component, value, assessmentVM);
            }

            assessmentVM.model.appealDeadline = appealDeadline ?? assessmentVM.model.appealDeadline;
        }

        return {
            disableApplyAll:
                postMappingData.disableApplyAll ||
                exemPostMappingData.disableApplyAll ||
                altPostMappingData.disableApplyAll,
            explanations: {
                av: postMappingData.explanations.av,
                fairMarketValue: postMappingData.explanations.fairMarketValue,
                exem: exemPostMappingData.explanations.exem,
                cap: altPostMappingData.explanations.cap,
                components: [
                    ...postMappingData.explanations.components,
                    ...exemPostMappingData.explanations.components,
                    ...altPostMappingData.explanations.components,
                ],
                lowConfidenceReasons,
            },
        };
    }

    private async _mapComponents(
        idpComponents: Weissman.Model.Workflow.DocumentIntake.Idp.Assessment.ComponentItem[],
        valueStr: string,
        assessmentVM: AssessmentViewModel,
        enabledComponents: AvailableComponent[],
        assessedValueAmount: number | undefined,
        { calculateValues, addRowValues }: AssessmentChildUpdates,
        otherComponents?: Weissman.Model.Workflow.DocumentIntake.Idp.Assessment.ComponentItem[],
        force?: boolean,
    ): Promise<PostMappingData> {
        console.log('idpComponents', idpComponents);
        console.log('valueStr', valueStr);
        console.log('enabledComponents', enabledComponents);
        console.log('assessedValueAmount', assessedValueAmount);
        console.log('other components', otherComponents);
        let disableApplyAll = false;
        const components = [];
        const lowConfidenceReasons = [];

        for (const idpComponent of idpComponents) {
            let explanation =
                "IDP could not find a match based on parcel and component, so no update was made and 'Apply All' has been disabled. ";

            const inUseComponent = find(assessmentVM.model.annualAssessmentDetails, (x) =>
                this._doComponentNamesMatch(x.componentName, idpComponent.name),
            );

            const otherIdpComponent = otherComponents?.find((x) => x.name === idpComponent.name);

            if (inUseComponent) {
                const fmvAddsUp = new Decimal(assessedValueAmount ?? 0)
                    .dividedBy(idpComponent.amount)
                    .toSignificantDigits(5)
                    .times(100)
                    .equals(inUseComponent.ratio);

                if (
                    idpComponents.length === 1 &&
                    !!assessedValueAmount &&
                    valueStr === 'fairMarketValue' &&
                    !fmvAddsUp
                ) {
                    if (force) {
                        inUseComponent.fairMarketValue = idpComponent.amount;
                        inUseComponent.fmvOverridden = true;
                    } else {
                        explanation =
                            'IDP component matched with AR component, but AV / FMV ≠ ratio. FMV will be overridden if Apply All is clicked.';
                        lowConfidenceReasons.push(
                            `IDP component ${inUseComponent.componentName} matched with AR component, but AV / FMV ≠ ratio`,
                        );
                    }
                } else {
                    let componentValuesAddUp = true;

                    if (otherIdpComponent) {
                        const av = valueStr === 'av' ? idpComponent.amount : otherIdpComponent?.amount;
                        const fmv = valueStr === 'fairMarketValue' ? idpComponent.amount : otherIdpComponent?.amount;

                        componentValuesAddUp = new Decimal(av)
                            .dividedBy(fmv)
                            .toSignificantDigits(5)
                            .times(100)
                            .equals(inUseComponent.ratio);

                        if (!componentValuesAddUp) {
                            if (force) {
                                inUseComponent.fairMarketValue = fmv;
                                inUseComponent.assessedValue = av;
                                inUseComponent.fmvOverridden = true;
                            } else {
                                explanation =
                                    'IDP component matched with AR component, but AV / FMV ≠ ratio. FMV will be overridden if Apply All is clicked.';
                                lowConfidenceReasons.push(
                                    `IDP component ${inUseComponent.componentName} matched with AR component, but AV / FMV ≠ ratio`,
                                );
                            }
                        }
                    }

                    if (componentValuesAddUp) {
                        calculateValues.push({ valueStr, component: inUseComponent, amount: idpComponent.amount });
                        explanation =
                            'Component value matched perfectly with the AR component, so it was applied as provided.';
                    }
                }
            } else {
                const availableComponent = find(enabledComponents, (x) =>
                    this._doComponentNamesMatch(x.componentName, idpComponent.name),
                );

                if (availableComponent) {
                    addRowValues.push({ component: availableComponent, value: { [valueStr]: idpComponent.amount } });
                    explanation =
                        'This component type is allowed for this assessment based on the state and property type, so the value was mapped successfully.';
                } else {
                    disableApplyAll = true;
                    lowConfidenceReasons.push(
                        `IDP component ${idpComponent.name} could not be matched with an enabled AR component`,
                    );
                }
            }

            components.push({
                name: idpComponent.name,
                explanation,
            });
        }

        // Commenting out because we dont' need to remove components that weren't in IDP?
        // assessmentVM.model.annualAssessmentDetails = filter(assessmentVM.model.annualAssessmentDetails, x => {
        //     return x.assessmentComponentTypeID === EXEM_COMPONENT_TYPE || some(idpComponents, { name: x.componentName }) // T
        // })

        return {
            disableApplyAll,
            explanations: {
                components,
                lowConfidenceReasons,
            },
        };
    }

    private _doComponentNamesMatch(ppComponentName: string, idpComponentName: string): boolean {
        const idpComponentNameWords = words(idpComponentName.toLowerCase());
        const ppComponentNameWords = words(ppComponentName.toLowerCase());

        if (includes(idpComponentNameWords, 'cap')) {
            return includes(ppComponentNameWords, 'cap');
        }

        if (ppComponentName === 'Land (Ag)') {
            return 'Ag' === idpComponentName;
        }

        return idpComponentName === ppComponentName;
    }

    private async _divvyUpIdpTotal(
        totalAmount: number,
        assessmentVM: AssessmentViewModel,
        enabledComponents: AvailableComponent[],
        calculateStr: string,
        componentStr: string,
        propertyTypeId: number,
        componentType: number,
        { calculateValues, addRowValues }: AssessmentChildUpdates,
    ): Promise<PostMappingData> {
        let disableApplyAll = false;
        let explanation = '';
        const lowConfidenceReasons = [];

        // filter by component type (either normal or Exem)
        const componentsOfCertainType = filter(assessmentVM.model.annualAssessmentDetails, {
            assessmentComponentTypeID: componentType,
        });

        switch (componentsOfCertainType.length) {
            case 0:
                let propTypeComponent;

                if (componentType === NORMAL_COMPONENT_TYPE) {
                    // if there are no current components, add component based on property type and apply total to that
                    propTypeComponent = find(enabledComponents, {
                        assessmentComponentID: PROP_TYPE_COMP_MAP[propertyTypeId],
                    });

                    explanation =
                        'With no components available from IDP, the total is routed to on AR component based on property type — like RE to Imps or PP to Pers Prop.';
                } else {
                    propTypeComponent = find(enabledComponents, {
                        assessmentComponentID: EXTRA_COMP_MAP[componentType], // Exem or Cap
                    });

                    explanation =
                        componentType === EXEM_COMPONENT_TYPE
                            ? 'No components were found for this assessment. The total amount is applied to Exem.'
                            : 'No components found — total was applied to Cap';
                }

                if (propTypeComponent) {
                    addRowValues.push({ component: propTypeComponent, value: { [calculateStr]: totalAmount } });
                } else {
                    // there is no enabled prop type component. Unclear what to do
                    disableApplyAll = true;
                    lowConfidenceReasons.push(`AR component could not be found for IDP component to map to`);
                }
                break;

            case 1:
                // if there is only one component existing in the assessment revision map the total value from IDP to that component.
                calculateValues.push({
                    valueStr: calculateStr,
                    component: componentsOfCertainType[0],
                    amount: totalAmount,
                });
                explanation =
                    componentType === NORMAL_COMPONENT_TYPE
                        ? 'Only one component is available for this assessment — the total from IDP is expected to apply here'
                        : 'Only one component is available for this assessment. The total amount is applied directly to that component.';
                break;

            case 2:
                if (propertyTypeId === PropertyType.RealEstate && componentType === NORMAL_COMPONENT_TYPE) {
                    // If there are two or more components existing in the assessment revision and one is "Land",
                    // keep land the same value and make the other components (normally just Imps) = Total from IDP - the value on Land.
                    const landComponent = find(componentsOfCertainType, {
                        componentName: 'Land',
                    });

                    if (landComponent) {
                        const totalMinusLand = new Decimal(totalAmount)
                            .minus(landComponent[componentStr] ?? 0)
                            .toNumber();

                        if (totalMinusLand >= 0) {
                            const dividedLeftoverAmountWholeNumber = new Decimal(totalMinusLand)
                                .dividedBy(componentsOfCertainType.length - 1)
                                .floor()
                                .toNumber();

                            let remainder = totalMinusLand % (componentsOfCertainType.length - 1);

                            componentsOfCertainType.forEach((component) => {
                                let remainderToAdd = 0;
                                if (remainder > 0) {
                                    remainder--;
                                    remainderToAdd++;
                                }
                                if (component.componentName !== 'Land') {
                                    const newValue = new Decimal(dividedLeftoverAmountWholeNumber)
                                        .add(remainderToAdd)
                                        .toNumber();
                                    calculateValues.push({ valueStr: calculateStr, component, amount: newValue });

                                    explanation =
                                        'For RE parcels with Land and one other component, anything above the Land value is applied to the second component — Land stays the same';
                                }
                            });
                        } else {
                            // If somehow the Land > Total from IDP then do nothing.
                            disableApplyAll = true;
                            lowConfidenceReasons.push('Land amount is greater than total from IDP');
                        }
                    } else {
                        // There is no land component. Unclear what to do
                        disableApplyAll = true;
                        lowConfidenceReasons.push('There are exactly two components, but there is no Land component');
                    }
                } else {
                    disableApplyAll = true;
                    if (componentType === NORMAL_COMPONENT_TYPE) {
                        lowConfidenceReasons.push(
                            'There are exactly two components, but the property type is not Real Estate',
                        );
                    }
                }

                break;

            default:
                disableApplyAll = true;
                lowConfidenceReasons.push('There are more than 2 components on the AR, so the mapping is unclear');
                break;
        }

        if (disableApplyAll) {
            explanation =
                "IDP could not find a match based on parcel and component, so no updates were made and 'Apply All' has been disabled.";
        }

        const explanations: MappingExplanations = {
            components: [],
            lowConfidenceReasons,
        };

        switch (componentType) {
            case NORMAL_COMPONENT_TYPE:
                explanations[calculateStr] = explanation;
                break;
            case EXEM_COMPONENT_TYPE:
                explanations.exem = explanation;
                break;
            case ALT_COMPONENT_TYPE:
                explanations.cap = explanation;
                break;
        }

        return {
            disableApplyAll,
            explanations,
        };
    }

    // private async _addOrApplyComponentValue(
    //     availableComponent: any,
    //     valueObj: ValueMessagePayload,
    //     annualAssessmentDetails: AnnualAssessmentDetail[],
    //     assessmentChild: AssessmentComponent,
    //     valueStr: string
    // ): Promise<void> {
    //     const idpComponent = find(valueObj.components, {
    //         name: availableComponent.componentName
    //     })

    //     if(isNumber(idpComponent?.amount)) {
    //         const inUseComponent = find(annualAssessmentDetails, {
    //             componentName: idpComponent.name
    //         })

    //         if(inUseComponent) {
    //             assessmentChild.calculate(valueStr, inUseComponent, idpComponent.amount)
    //         } else {
    //             await assessmentChild.addRow(availableComponent, { [valueStr]: idpComponent.amount })
    //         }
    //     }
    // }

    private _mapBillFromIdp(
        bill: Bill,
        idpResults: Weissman.Model.Workflow.DocumentIntake.OcrResponse.API.TaxBillAPIResponse,
        paymentsFromModal?: IdpCard[],
    ): void {
        if (bill.payments.length === 1 && !some(idpResults.installments, (x) => x.details.length === 1)) {
            const idpPayments = flatMap(idpResults.installments, 'details') as DetailsInstallmentMessageItem[];
            const idpPayment = minBy(idpPayments, (x) => new Date(x.dueDate));

            if (!idpPayment) return;

            let fieldsToApply = ['dueDate', 'grossPayment'];

            if (paymentsFromModal) {
                const paymentFromModal = paymentsFromModal[0];

                if (!paymentFromModal) {
                    console.warn(`No payment from modal found from modal for single payment bill`);
                    return;
                }

                fieldsToApply = paymentFromModal.details.filter((x) => x.apply).map((x) => x.field);
            }

            if (fieldsToApply.length === 0) return;

            bill.billAmount = idpResults.totalAmount || 0;
            bill.directAsmt = idpResults.directAssessmentAmount || 0;

            if (fieldsToApply.includes('dueDate')) {
                bill.payments[0].dueDate = idpPayment.dueDate;
            }
            if (fieldsToApply.includes('grossPayment')) {
                bill.payments[0].grossPayment = idpResults.totalAmount;
            }

            bill.payments[0].paymentAmount = this._billClusterService.paymentPropChanged(bill.payments[0]);
            bill.calcProjected = false;

            return;
        }

        bill.billAmount = idpResults.totalAmount || 0;
        bill.directAsmt = idpResults.directAssessmentAmount || 0;

        const { installment, discountIndex } = this._getInstallmentAndDiscountIdx(idpResults.installments, bill);

        bill.payments.forEach((payment: Payment, pmtIdx: number) => {
            const ocrPayment = installment?.details[pmtIdx];

            if (!ocrPayment) return;

            let fieldsToApply: string[] = this.cardValues.map((x) => x.field);

            if (paymentsFromModal) {
                const paymentFromModal = paymentsFromModal[pmtIdx];

                if (!paymentFromModal) {
                    console.warn(`No payment from modal found from modal for payment index ${pmtIdx}`);
                    return;
                }

                fieldsToApply = paymentFromModal.details.filter((x) => x.apply).map((x) => x.field);
            }

            if (fieldsToApply.length === 0) return;

            fieldsToApply.forEach((field) => {
                switch (field) {
                    case 'dueDate':
                        payment.dueDate = ocrPayment?.discount[discountIndex]?.dueDate || ocrPayment.dueDate;
                        break;
                    case 'grossPayment':
                        payment.grossPayment = ocrPayment?.amount || 0;
                        break;
                    case 'discountAmount':
                        payment.discountAmount = -this.getDiscount(ocrPayment, discountIndex);
                        break;
                    case 'interestAmount':
                        payment.interestAmount = ocrPayment?.interest[discountIndex]?.interestAmount || 0;
                        break;
                    case 'penaltyAmount':
                        payment.penaltyAmount = this.getValidIDPPenalty(ocrPayment?.penalty, payment.dueDate);
                        break;
                }
            });

            payment.paymentAmount = this._billClusterService.paymentPropChanged(payment);
            bill.calcProjected = false;
        });
    }

    getValidIDPPenalty(
        penalties: Weissman.Model.Workflow.DocumentIntake.OcrResponse.API.InstallmentDetailsPenaltyAPIItem[],
        paymentDueDate: Date,
    ): number {
        const validPenalties = penalties.filter((x) => x.fromDate && x.fromDate <= paymentDueDate);
        const latestFromDatePenalty = maxBy(validPenalties, (x) => new Date(x.fromDate).getTime());

        return latestFromDatePenalty?.penaltyAmount || 0;
    }

    private _getInstallmentAndDiscountIdx(
        installments: Weissman.Model.Workflow.DocumentIntake.OcrResponse.API.InstallmentAPIItem[],
        bill: Bill,
    ): {
        installment: Weissman.Model.Workflow.DocumentIntake.OcrResponse.API.InstallmentAPIItem;
        discountIndex: number;
    } {
        const installment = installments.find((x) => x.details.length === bill.payments.length);
        let discountIndex = 0;

        if (bill.payments.length === 1) {
            const ocrPayment = installment?.details.find((x) =>
                moment(x.dueDate).isSame(moment(bill.payments[0].dueDate)),
            );
            discountIndex = ocrPayment?.discount.findIndex((x) => x.dueDate === bill.payments[0].dueDate) || 0;
        }

        return { installment, discountIndex };
    }

    // private _getDirectAssessment(idpResults: Core.TaxBillPushMessageDto): number {
    //     if(idpResults.directAssessmentAmount) {
    //         return idpResults.directAssessmentAmount;
    //     } else {
    //         return reduce(idpResults.taxAuthorities, (sum, ta) => {
    //             if(!ta.taxRate) {
    //                 sum += ta.taxAmount;
    //             }

    //             return sum;
    //         }, 0);
    //     }
    // }
}
